import React, { FC, useEffect } from 'react';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import { makeStyles, Theme, createStyles } from '@material-ui/core';
import { useTranslation } from '../../../../../hooks/useTranslation';
import PhoneTextField from '../../../../phone-text-field/index';
import colors from '../../../../../themes/main-colors';
import useFormState from '../../../../../hooks/useFormState';
import validationRules, { Model } from './callBackNotification.rules';
import { parsePhoneNumber } from '../../../../../util/format';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    locationText: {
      paddingBottom: theme.spacing(0.5),
      fontSize: '1rem',
      color: colors.darkBlue,
      fontWeight: 600,
      lineHeight: '1.75rem',
      [theme.breakpoints.down('sm')]: {
        fontWeight: 'normal',
      },
    },
    labelText: {
      fontSize: '.875rem',
      fontWeight: 600,
      paddingTop: '.75rem',
      paddingBottom: '1px',
      [theme.breakpoints.down('sm')]: {
        marginLeft: '40px',
      },
    },
    checkBoxStyles: {
      alignItems: 'baseline',
      marginTop: '16px',
      marginLeft: '-11px',
      height: '100%',
      [theme.breakpoints.down('sm')]: {
        padding: 0,
        width: '28px',
        height: '28px',
        marginTop: '10px',
        marginRight: '10px',
        '& .MuiSvgIcon-root': {
          width: '100%',
          height: '100%',
          fill: colors.aluminiumColor,
        },
      },
    },
    notificationField: {
      width: '100%',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        alignItems: 'center',
      },
    },
  }),
);

const defaultFormValues: Model = {
  callbackPhoneNumber: '',
  receiveCallBack: true,
};

type Props = {
  onChange: (checked: boolean, phone: string, error: boolean) => void;
  primaryContactNumber: string;
  isFindAccount?: boolean;
};

const CallbackNotification: FC<Props> = ({
  onChange,
  primaryContactNumber,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const form = useFormState(defaultFormValues, {
    validate: validationRules,
  });
  useEffect(() => {
    void form.setValue(
      'callbackPhoneNumber',
      parsePhoneNumber(primaryContactNumber),
    );
  }, [primaryContactNumber]);
  useEffect(() => {
    onChange(
      form.values.receiveCallBack,
      form.values.callbackPhoneNumber,
      Boolean(form.errors.callbackPhoneNumber),
    );
  }, [form]);

  const onPasteHandle = async (e: any) => {
    const value = e.clipboardData.getData('text/plain');
    await form.setValue('callbackPhoneNumber', parsePhoneNumber(value));
  };

  const onCopyHandle = async (e: any) => {
    await e.clipboardData.setData('text/plain', e.target.value);
  };

  const onCutHandle = async (e: any) => {
    await e.clipboardData.setData('text/plain', e.target.value);
  };

  const onChangeInputHandle = async (e: any) => {
    await form.setValue(
      'callbackPhoneNumber',
      parsePhoneNumber(e.target.value),
    );
  };

  return (
    <div data-testid={'notification-options'}>
      <Box
        display={'flex'}
        flexDirection={'row'}
        justifyContent={'flex-start'}
        className={classes.notificationField}
      >
        <Checkbox
          name="receiveCallBack"
          size={'small'}
          checked={form.values.receiveCallBack}
          color={'primary'}
          onChange={form.onChange}
          inputProps={{ 'aria-label': 'primary checkbox' }}
          className={classes.checkBoxStyles}
        />
        <PhoneTextField
          size={'small'}
          placeholder={t('PHONE_NUMBER')}
          disabled={!form.values.receiveCallBack}
          label={''}
          key={String(form.values.receiveCallBack)}
          data-testid={'callback-phone-number'}
          name={'callbackPhoneNumber'}
          autoComplete={'telephone'}
          type={'number'}
          inputProps={{ type: 'telephone' }}
          {...form.props('callbackPhoneNumber')}
          helperText={
            form.values.receiveCallBack && form.errors.callbackPhoneNumber
          }
          value={form.values.callbackPhoneNumber}
          onPaste={async (e: any) => {
            await onPasteHandle(e);
          }}
          onCopy={async (e: any) => {
            await onCopyHandle(e);
          }}
          onCut={async (e: any) => {
            await onCutHandle(e);
          }}
          onFocus={async (e: any) => {
            await onChangeInputHandle(e);
          }}
        />
      </Box>
    </div>
  );
};

export default CallbackNotification;
